// src/components/StripeCheckoutButton.tsx
import React, { useState, useEffect } from 'react';
import { Button, Text } from '@aws-amplify/ui-react';
import { useStripe } from '../StripeContext';
import { generateClient } from 'aws-amplify/api';
import { StorageTier, SubscriptionAction } from '../API';
import { getStoragePriceForTier } from '../services/storageTierService';
import { useAuth } from '../AuthContext';

// GraphQL mutation for creating a checkout session
const createCheckoutSession = /* GraphQL */ `
  mutation CreateCheckoutSession(
    $tier: StorageTier!, 
    $successUrl: String, 
    $cancelUrl: String
  ) {
    createCheckoutSession(
      tier: $tier, 
      successUrl: $successUrl, 
      cancelUrl: $cancelUrl
    ) {
      sessionId
      url
    }
  }
`;

// Existing manageSubscription mutation
const manageSubscription = /* GraphQL */ `
  mutation ManageSubscription($subscriptionId: String!, $action: SubscriptionAction!) {
    manageSubscription(subscriptionId: $subscriptionId, action: $action) {
      subscriptionId
      status
      message
    }
  }
`;

// Standard updateUser mutation
const updateUser = /* GraphQL */ `
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      storageTier
      storageLimit
    }
  }
`;

const client = generateClient();

interface StripeCheckoutButtonProps {
    tier: StorageTier;
    text?: string;
    variant?: "primary" | "secondary" | "link" | "destructive";
    fullWidth?: boolean;
    backgroundColor?: string;
    color?: string;
    disabled?: boolean;
    onCheckoutStarted?: () => void;
    onCheckoutCompleted?: () => void;
    onError?: (error: Error) => void;
}

const StripeCheckoutButton: React.FC<StripeCheckoutButtonProps> = ({
    tier,
    text = "Subscribe",
    variant = "primary",
    fullWidth = false,
    backgroundColor,
    color = "white",
    disabled = false,
    onCheckoutStarted,
    onCheckoutCompleted,
    onError
}) => {
    const { stripe, isLoading: isStripeLoading } = useStripe();
    const { user } = useAuth(); // Get current user details
    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tierPrice, setTierPrice] = useState<number | null>(null);
    const [isLoadingPrice, setIsLoadingPrice] = useState(false);

    // Fetch the price for this tier when component mounts
    useEffect(() => {
        const loadTierPrice = async () => {
            try {
                setIsLoadingPrice(true);
                const price = await getStoragePriceForTier(tier);
                setTierPrice(price);
            } catch (err) {
                console.error('Error loading tier price:', err);
                setError('Unable to determine pricing for this tier');
            } finally {
                setIsLoadingPrice(false);
            }
        };

        loadTierPrice();
    }, [tier]);

    const handleFreeSubscription = async () => {
        try {
            // Check if user has an existing subscription that needs to be canceled
            if (user?.banjaraUser.subscriptionId) {
                await client.graphql({
                    query: manageSubscription,
                    variables: {
                        subscriptionId: user.banjaraUser.subscriptionId,
                        action: SubscriptionAction.CANCEL
                    },
                    authMode: "userPool"
                });
            }

            // Redirect to success page
            window.location.href = `${window.location.origin}/profile?payment=success&tier=${tier}`;
        } catch (err) {
            console.error('Error updating to free tier:', err);
            throw err;
        }
    };

    const handleSubscribe = async () => {
        if (tierPrice === null) {
            setError('Unable to determine pricing for this tier');
            return;
        }
        try {
            setIsCreatingSession(true);
            setError(null);

            if (onCheckoutStarted) {
                onCheckoutStarted();
            }
            // For free tiers (price is 0), directly update the user's tier
            if (tierPrice === 0) {
                await handleFreeSubscription();

                if (onCheckoutCompleted) {
                    onCheckoutCompleted();
                }
                return;
            }

            // Only check for stripe instance if it's a paid tier
            if (!stripe) {
                setError('Payment system unavailable at the moment');
                return;
            }

            // Generate current URL for successful return
            const successUrl = `${window.location.origin}/profile?payment=success&tier=${tier}`;
            const cancelUrl = `${window.location.origin}/profile?payment=cancelled`;

            // Create checkout session for paid tiers
            const response: any = await client.graphql({
                query: createCheckoutSession,
                variables: {
                    tier: tier,
                    successUrl,
                    cancelUrl
                },
                authMode: "userPool"
            });

            const { sessionId, url } = response.data.createCheckoutSession;

            // Redirect to checkout page
            window.location.href = url;

            if (onCheckoutCompleted) {
                onCheckoutCompleted();
            }
        } catch (err) {
            console.error('Error during subscription process:', err);
            setError('Failed to process your request. Please try again.');

            if (onError && err instanceof Error) {
                onError(err);
            }
        } finally {
            setIsCreatingSession(false);
        }
    };

    const isButtonDisabled = disabled || isStripeLoading || isCreatingSession || isLoadingPrice;

    return (
        <div>
            {error && (
                <Text color="red" fontSize="0.875rem" marginTop="0.5rem">
                    {error}
                </Text>
            )}
            <Button
                onClick={handleSubscribe}
                isDisabled={isButtonDisabled}
                isLoading={isCreatingSession || isLoadingPrice}
                loadingText={isLoadingPrice ? "Loading pricing..." : "Preparing checkout..."}
                variation="primary"
                width={fullWidth ? "100%" : undefined}
                backgroundColor={backgroundColor}
                color={color}
            >
                {tierPrice === 0 ? "Select" : text}
            </Button>


        </div>
    );
};

export default StripeCheckoutButton;